import { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { DEVICE_TYPE, getEpgUrl } from 'config/api';

import { EpgProgram } from 'store/railsBuilder/reducer.types';
import { RequestStatus, requestSuccess } from 'store/utils';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';

import {
    getEpg,
    getEpgByVisibleChannels,
    getEpgByVisibleChannelsRequest,
    getEpgByVisibleChannelsSuccess,
    getEpgFailure,
    getEpgRequest,
    getEpgSuccess,
    getPartialUpdateEpg,
    getPartialUpdateEpgFailure,
    getPartialUpdateEpgRequest,
    getPartialUpdateEpgSuccess,
    setEpgLastUpdated,
} from './actions';
import { EpgResponse } from './actions.types';
import {
    GET_EPG,
    GET_EPG_BY_VISIBLE_CHANNELS,
    GET_EPG_PARTIAL_UPDATE,
} from './constants';
import { EpgRequestedBy } from './reducer';
import { selectEpgParams, selectEpgRequestStatus } from './selectors';

function* onGetEpg({ payload }: ReturnType<typeof getEpg>) {
    try {
        const { start: prevStart, end: prevEnd } = yield select(
            selectEpgParams,
        );
        const start = payload.start || prevStart;
        const end = payload.end || prevEnd;
        const requestedBy = payload.requestedBy || EpgRequestedBy.SCREEN_ACCESS;

        yield put(
            getEpgRequest({
                start,
                end,
                requestedBy,
            }),
        );

        const {
            data: { results },
        }: AxiosResponse<EpgResponse> = yield call(
            NagraInstance.get,
            getEpgUrl({ from: start, to: end, deviceType: DEVICE_TYPE }),
        );

        const data = results.reduce<Record<string, EpgProgram[]>>(
            (acc, cur) => {
                acc[cur.channelId] = cur.programmes;

                return acc;
            },
            {},
        );

        yield put(getEpgSuccess({ data }));
    } catch (error) {
        yield put(getEpgFailure(error));
    }
}

function* onGetEpgByVisibleChannels({
    payload,
}: ReturnType<typeof getEpgByVisibleChannels>) {
    try {
        const { start, end, requestedBy } = yield select(selectEpgParams);
        const status: RequestStatus = yield select(selectEpgRequestStatus);
        const withLoading = !requestSuccess(status) || payload.withLoading;
        const { channels } = payload;

        if (withLoading) {
            yield put(
                getEpgRequest({
                    start,
                    end,
                    requestedBy,
                }),
            );
        } else {
            yield put(
                getEpgByVisibleChannelsRequest({
                    start,
                    end,
                    requestedBy,
                }),
            );
        }

        const results: PromiseAllSettledReturnValue<
            AxiosResponse<EpgResponse>
        > = yield call(
            [Promise, 'allSettled'],
            channels.map((id) =>
                NagraInstance.get(
                    getEpgUrl({
                        from: start,
                        to: end,
                        channel: id,
                        deviceType: DEVICE_TYPE,
                    }),
                ),
            ),
        );

        const data = results.reduce<Record<string, EpgProgram[]>>(
            (acc, cur, currentIndex) => {
                if (cur.status === 'fulfilled') {
                    acc[cur.value.data.results?.[0].channelId] =
                        cur.value.data.results?.[0].programmes;
                }

                if (cur.status === 'rejected') {
                    acc[channels[currentIndex]] = [];
                }

                return acc;
            },
            {},
        );

        if (withLoading) {
            yield put(getEpgSuccess({ data }));
        } else {
            yield put(getEpgByVisibleChannelsSuccess({ data }));
        }

        yield put(setEpgLastUpdated(Date.now()));
    } catch (error) {
        yield put(getEpgFailure(error));
    }
}

function* onGetEpgPartialUpdate({
    payload,
}: ReturnType<typeof getPartialUpdateEpg>) {
    try {
        const { start, end, channelId } = payload;

        yield put(getPartialUpdateEpgRequest(payload));

        const {
            data: { results },
        }: AxiosResponse<EpgResponse> = yield call(
            NagraInstance.get,
            getEpgUrl({
                from: start,
                to: end,
                channel: channelId,
                deviceType: DEVICE_TYPE,
            }),
        );

        const data = results.reduce<Record<string, EpgProgram[]>>(
            (acc, cur) => {
                acc[cur.channelId] = cur.programmes;

                return acc;
            },
            {},
        );

        yield put(getPartialUpdateEpgSuccess({ start, end, data }));
    } catch (error) {
        yield put(getPartialUpdateEpgFailure(error));
    }
}

export default [
    takeLatest(GET_EPG, onGetEpg),
    takeEvery(GET_EPG_BY_VISIBLE_CHANNELS, onGetEpgByVisibleChannels),
    takeLatest(GET_EPG_PARTIAL_UPDATE, onGetEpgPartialUpdate),
];
